import React, { useEffect, useState } from 'react';
import {
    Typography,
    Button,
    Form,
    Input,
    Modal,
    Space,
    Card,
    App,
    Alert,
    Divider,
    Row,
    Col, Spin, QRCode
} from 'antd';
import {
    LockOutlined,
    MailOutlined,
    KeyOutlined,
    EditOutlined,
    LogoutOutlined,
    ReloadOutlined, CopyOutlined
} from '@ant-design/icons';
import { authService, securityService } from '../services/api';
import { usePageTitle } from "../context/PageTitleContext";
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Password } = Input;

// Компонент для ввода OTP кода
const OtpInput = ({ value = '', onChange, length = 6 }) => {
    const inputRefs = Array(length).fill(0).map(() => React.createRef());

    const handleChange = (e, index) => {
        const val = e.target.value;
        if (val.length <= 1) {
            const newValue = value.split('');
            newValue[index] = val;
            onChange(newValue.join(''));

            // Автоматический переход к следующему полю при вводе
            if (val && index < length - 1) {
                inputRefs[index + 1].current.focus();
            }
        }
    };

    const handleKeyDown = (e, index) => {
        // При нажатии Backspace, если поле пустое, фокус на предыдущее поле
        if (e.key === 'Backspace' && !value[index] && index > 0) {
            inputRefs[index - 1].current.focus();
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
            {Array(length).fill(0).map((_, index) => (
                <Input
                    key={index}
                    ref={inputRefs[index]}
                    value={value[index] || ''}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    style={{ width: 50, height: 50, textAlign: 'center', fontSize: 20 }}
                    maxLength={1}
                />
            ))}
        </div>
    );
};

const SecurityPage = () => {
    const { message } = App.useApp();
    const { setPageTitle } = usePageTitle();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [twoFAInfo, setTwoFAInfo] = useState(null);
    const [twoFALoading, setTwoFALoading] = useState(true);
    const [twoFAModalVisible, setTwoFAModalVisible] = useState(false);
    const [twoFADisableModalVisible, setTwoFADisableModalVisible] = useState(false);
    const [twoFASetupData, setTwoFASetupData] = useState(null);
    const [twoFACode, setTwoFACode] = useState('');
    const [twoFASecret, setTwoFASecret] = useState('');
    const [qrCodeLoading, setQrCodeLoading] = useState(false);

    // Получаем текущего пользователя
    const currentUser = authService.getCurrentUser();

    // Формы
    const [passwordForm] = Form.useForm();
    const [emailForm] = Form.useForm();
    const [otpForm] = Form.useForm();

    // Состояние для генерируемого пароля
    const [generatedPassword, setGeneratedPassword] = useState('');

    // Состояния модальных окон
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [emailModalVisible, setEmailModalVisible] = useState(false);
    const [otpModalVisible, setOtpModalVisible] = useState(false);
    const [logoutModalVisible, setLogoutModalVisible] = useState(false);

    // Состояния для OTP верификации
    const [otpValue, setOtpValue] = useState('');
    const [expiresAt, setExpiresAt] = useState(null);
    const [timeLeft, setTimeLeft] = useState('');
    const [newEmail, setNewEmail] = useState('');

    useEffect(() => {
        setPageTitle('Безопасность');
        loadTwoFAInfo();

        return () => {
            setPageTitle('');
        };
    }, [setPageTitle]);

    // Обработчик истечения времени OTP
    useEffect(() => {
        if (!expiresAt) return;

        const calculateTimeLeft = () => {
            const expiry = new Date(expiresAt);
            const now = new Date();
            const diff = expiry - now;

            if (diff <= 0) {
                setTimeLeft('Истекло');
                return null;
            }

            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        };

        setTimeLeft(calculateTimeLeft() || 'Истекло');
        const timer = setInterval(() => {
            const remaining = calculateTimeLeft();
            if (!remaining) {
                clearInterval(timer);
                setTimeLeft('Истекло');
            } else {
                setTimeLeft(remaining);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [expiresAt]);

    const loadTwoFAInfo = async () => {
        try {
            setTwoFALoading(true);
            const userId = currentUser?.id;
            if (!userId) {
                return;
            }

            const response = await securityService.get2FAInfo(userId);
            if (response.status === 'success') {
                setTwoFAInfo(response.data);
            } else {
                setTwoFAInfo(null);
            }
        } catch (error) {
            setTwoFAInfo(null);
        } finally {
            setTwoFALoading(false);
        }
    };

    // Функция для настройки 2FA
    const setupTwoFA = async () => {
        try {
            setQrCodeLoading(true);
            const response = await securityService.setup2FA();
            setTwoFASetupData(response.data);
            setTwoFASecret(response.data.secret);
            setTwoFAModalVisible(true);
        } catch (error) {
            // Проверяем наличие данных в ответе ошибки
            if (error.response && error.response.data) {
                message.error(error.response.data.message || 'QR-код не может быть сгенерирован');
            } else {
                message.error('Произошла ошибка при настройке 2FA');
            }
            setTwoFASetupData(null);
        } finally {
            setQrCodeLoading(false);
        }
    };

// Функция для верификации и активации 2FA
    const verifyAndActivateTwoFA = async () => {
        if (twoFACode.length !== 6) {
            message.error('Введите полный код из 6 цифр');
            return;
        }

        try {
            setLoading(true);
            const response = await securityService.verify2FA(twoFACode, twoFASecret);

            message.success(response.message);
            setTwoFAModalVisible(false);
            setTwoFACode('');
            loadTwoFAInfo(); // Обновляем информацию о 2FA
        } catch (error) {
            // Проверяем наличие данных в ответе ошибки
            if (error.response && error.response.data) {
                message.error(error.response.data.message || 'Ошибка при верификации кода');
            } else {
                message.error('Произошла ошибка при верификации кода');
            }
            setTwoFACode('');
        } finally {
            setLoading(false);
        }
    };

    // Функция для отключения 2FA
    const disableTwoFA = async () => {
        if (twoFACode.length !== 6) {
            message.error('Введите полный код из 6 цифр');
            return;
        }

        try {
            setLoading(true);
            const response = await securityService.disable2FA(twoFACode);

            message.success(response.message);
            setTwoFADisableModalVisible(false);
            setTwoFACode('');
            loadTwoFAInfo(); // Обновляем информацию о 2FA
        } catch (error) {
            // Проверяем наличие данных в ответе ошибки
            if (error.response && error.response.data) {
                message.error(error.response.data.message || 'Ошибка при отключении 2FA');
            } else {
                message.error('Произошла ошибка при отключении 2FA');
            }
            setTwoFACode('');
        } finally {
            setLoading(false);
        }
    };

    // Функция для копирования секретного ключа
    const copySecretToClipboard = () => {
        if (twoFASecret) {
            navigator.clipboard.writeText(twoFASecret)
                .then(() => message.success('Секретный ключ скопирован'))
                .catch(err => message.error('Не удалось скопировать ключ'));
        }
    };

    // Обработчик для модального окна настройки 2FA
    const handleTwoFAModalCancel = () => {
        setTwoFAModalVisible(false);
        setTwoFACode('');
        setTwoFASetupData(null);
    };

    // Обработчик для модального окна отключения 2FA
    const handleTwoFADisableModalCancel = () => {
        setTwoFADisableModalVisible(false);
        setTwoFACode('');
    };

    // Функция генерации пароля
    const generatePassword = () => {
        // Генерация пароля в стиле macOS Password app
        const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
        const numberChars = '0123456789';
        const specialChars = '-._@+?!#$%^&*()';

        const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;

        // Минимум 1 символ из каждой категории
        let newPassword =
            uppercaseChars.charAt(Math.floor(Math.random() * uppercaseChars.length)) +
            lowercaseChars.charAt(Math.floor(Math.random() * lowercaseChars.length)) +
            numberChars.charAt(Math.floor(Math.random() * numberChars.length)) +
            specialChars.charAt(Math.floor(Math.random() * specialChars.length));

        // Добавляем случайные символы до нужной длины (14 символов)
        const length = 14;
        for (let i = newPassword.length; i < length; i++) {
            newPassword += allChars.charAt(Math.floor(Math.random() * allChars.length));
        }

        // Перемешиваем пароль
        newPassword = newPassword.split('').sort(() => 0.5 - Math.random()).join('');

        // Обновляем состояние для пароля
        setGeneratedPassword(newPassword);

        // Устанавливаем значения полей формы
        const formValues = {
            new_password: newPassword,
            confirm_password: newPassword
        };

        // Принудительно сбрасываем форму и устанавливаем новые значения
        setTimeout(() => {
            passwordForm.resetFields();
            passwordForm.setFieldsValue(formValues);
        }, 0);

        // Сообщение о генерации пароля
        message.success('Пароль успешно сгенерирован');
    };

    // Обработчик изменения пароля
    const handlePasswordChange = async (values) => {
        try {
            setLoading(true);
            const { old_password, new_password } = values;

            const response = await securityService.changePassword(old_password, new_password);

            if (response.success) {
                message.success(response.message);
                setPasswordModalVisible(false);
                passwordForm.resetFields();
                setGeneratedPassword('');
            } else {
                message.error(response.message);
            }
        } catch (error) {
            message.error('Произошла ошибка при изменении пароля');
        } finally {
            setLoading(false);
        }
    };

    // Обработчик запроса на изменение email
    const handleEmailChangeRequest = async (values) => {
        try {
            setLoading(true);
            const { old_email, new_email } = values;

            const response = await securityService.requestEmailChange(old_email, new_email);

            if (response.success) {
                message.success(response.message);
                setNewEmail(new_email); // Сохраняем новый email для отображения
                setExpiresAt(response.expiresAt);
                setEmailModalVisible(false);
                setOtpModalVisible(true);
            } else {
                message.error(response.message);
            }
        } catch (error) {
            message.error('Произошла ошибка при запросе на изменение почты');
        } finally {
            setLoading(false);
        }
    };

    // Обработчик верификации кода OTP
    const handleOtpVerify = async () => {
        if (otpValue.length !== 6) {
            message.error('Введите полный код');
            return;
        }

        try {
            setLoading(true);
            const response = await securityService.verifyEmailChange(otpValue);

            if (response.success) {
                message.success(response.message);
                setOtpModalVisible(false);
                setOtpValue('');
                // Обновить email в форме, если она всё ещё открыта
                emailForm.setFieldValue('old_email', response.email);
            } else {
                message.error(response.message);
            }
        } catch (error) {
            message.error('Произошла ошибка при проверке кода');
        } finally {
            setLoading(false);
        }
    };

    // Обработчик выхода из аккаунта
    const handleLogout = () => {
        setLogoutModalVisible(true);
    };

    // Подтверждение выхода из аккаунта
    const confirmLogout = () => {
        authService.logout(navigate);
        setLogoutModalVisible(false);
    };

    // Сброс форм при закрытии модальных окон
    const handlePasswordModalCancel = () => {
        passwordForm.resetFields();
        setPasswordModalVisible(false);
        setGeneratedPassword('');
    };

    const handleEmailModalCancel = () => {
        emailForm.resetFields();
        setEmailModalVisible(false);
    };

    const handleOtpModalCancel = () => {
        // Обычно не даем отменить процесс верификации OTP
        // Но можно добавить подтверждение
        Modal.confirm({
            title: 'Отменить изменение почты?',
            content: 'Процесс изменения почты будет прерван. Продолжить?',
            okText: 'Да, отменить',
            cancelText: 'Нет, вернуться',
            onOk: () => {
                setOtpModalVisible(false);
                setOtpValue('');
            }
        });
    };

    return (
        <>
            {/* Блок изменения логина */}
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <KeyOutlined style={{ marginRight: 8 }} />
                        <span>Логин аккаунта</span>
                    </div>
                }
                style={{ marginBottom: 24 }}
                extra={<Button type="primary" disabled={true}>Изменить</Button>}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text style={{ fontSize: 18 }}>{currentUser?.username || 'Не указан'}</Text>
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Логин нельзя изменить
                </Text>
            </Card>

            {/* Блок изменения пароля */}
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LockOutlined style={{ marginRight: 8 }} />
                        <span>Пароль</span>
                    </div>
                }
                style={{ marginBottom: 24 }}
                extra={<Button type="primary" onClick={() => setPasswordModalVisible(true)}>Изменить</Button>}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text style={{ fontSize: 18 }}>******</Text>
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Рекомендуем периодически менять пароль
                </Text>
            </Card>

            {/* Блок изменения email */}
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <MailOutlined style={{ marginRight: 8 }} />
                        <span>Email</span>
                    </div>
                }
                style={{ marginBottom: 24 }}
                extra={<Button
                    type="primary"
                    onClick={() => {
                        emailForm.setFieldValue('old_email', currentUser?.email || '');
                        setEmailModalVisible(true);
                    }}
                >
                    Изменить
                </Button>}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text style={{ fontSize: 18 }}>{currentUser?.email || 'Не указан'}</Text>
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Email используется для уведомлений
                </Text>
            </Card>

            {/* Блок настройки двухфакторной аутентификации */}
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <KeyOutlined style={{ marginRight: 8 }} />
                        <span>Двухфакторная аутентификация</span>
                    </div>
                }
                style={{ marginBottom: 24 }}
                extra={
                    twoFALoading ? (
                        <Button type="primary" loading>Загрузка...</Button>
                    ) : twoFAInfo?.is_active ? (
                        <Button type="primary" danger onClick={() => setTwoFADisableModalVisible(true)}>Отключить</Button>
                    ) : (
                        <Button type="primary" onClick={setupTwoFA}>Активировать</Button>
                    )
                }
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text style={{ fontSize: 18 }}>
                        {twoFALoading ? 'Загрузка...' :
                            twoFAInfo?.is_active ? 'Активирована' : 'Не активирована'}
                    </Text>
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Двухфакторная аутентификация обеспечивает дополнительный уровень защиты вашего аккаунта
                </Text>
            </Card>

            {/* Блок выхода из аккаунта */}
            <Card style={{ marginBottom: 24 }}>
                <Row align="middle" justify="space-between">
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>Выход из аккаунта</Title>
                        <Text type="secondary">
                            Это действие приведет к выходу из текущего сеанса
                        </Text>
                    </Col>
                    <Col>
                        <Button
                            danger
                            icon={<LogoutOutlined />}
                            onClick={handleLogout}
                        >
                            Выйти
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Модальное окно изменения пароля */}
            <Modal
                title="Изменение пароля"
                open={passwordModalVisible}
                onCancel={handlePasswordModalCancel}
                footer={null}
                maskClosable={false}
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handlePasswordChange}
                >
                    <Form.Item
                        name="old_password"
                        label="Текущий пароль"
                        rules={[
                            { required: true, message: 'Пожалуйста, введите текущий пароль' }
                        ]}
                    >
                        <Password placeholder="Введите текущий пароль" />
                    </Form.Item>

                    <Form.Item
                        name="new_password"
                        label="Новый пароль"
                        rules={[
                            { required: true, message: 'Пожалуйста, введите новый пароль' },
                            { min: 8, message: 'Пароль должен содержать не менее 8 символов' }
                        ]}
                        extra="Нажмите на кнопку справа, чтобы сгенерировать надежный пароль"
                    >
                        <Space.Compact style={{ width: '100%' }}>
                            <Password
                                prefix={<LockOutlined />}
                                placeholder="Введите новый пароль"
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    generatePassword();
                                }}
                                type="primary"
                            />
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item
                        name="confirm_password"
                        label="Подтверждение пароля"
                        dependencies={['new_password']}
                        rules={[
                            { required: true, message: 'Пожалуйста, подтвердите пароль' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('new_password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Пароли не совпадают'));
                                },
                            }),
                        ]}
                    >
                        <Password placeholder="Подтвердите новый пароль" />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handlePasswordModalCancel}>
                                Отмена
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                            >
                                Сохранить
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Модальное окно изменения email */}
            <Modal
                title="Изменение email"
                open={emailModalVisible}
                onCancel={handleEmailModalCancel}
                footer={null}
                maskClosable={false}
            >
                <Form
                    form={emailForm}
                    layout="vertical"
                    onFinish={handleEmailChangeRequest}
                >
                    <Form.Item
                        name="old_email"
                        label="Текущий email"
                        rules={[
                            { required: true, message: 'Пожалуйста, введите текущий email' },
                            { type: 'email', message: 'Пожалуйста, введите корректный email' }
                        ]}
                    >
                        <Input disabled placeholder="Текущий email" />
                    </Form.Item>

                    <Form.Item
                        name="new_email"
                        label="Новый email"
                        rules={[
                            { required: true, message: 'Пожалуйста, введите новый email' },
                            { type: 'email', message: 'Пожалуйста, введите корректный email' }
                        ]}
                    >
                        <Input placeholder="Введите новый email" />
                    </Form.Item>

                    <Alert
                        message="Внимание"
                        description="На новый email будет отправлен код подтверждения"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleEmailModalCancel}>
                                Отмена
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                            >
                                Далее
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Модальное окно ввода OTP кода */}
            <Modal
                title="Подтверждение email"
                open={otpModalVisible}
                onCancel={handleOtpModalCancel}
                footer={null}
                closable={false}
                maskClosable={false}
            >
                <div style={{ textAlign: 'center' }}>
                    <Alert
                        message={`Код подтверждения отправлен на ${newEmail}`}
                        description={
                            <div>
                                Введите 6-значный код, отправленный на указанный email.
                                <br />
                                Время действия кода: <strong>{timeLeft}</strong>
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 24, textAlign: 'left' }}
                    />

                    <div style={{ marginBottom: 24 }}>
                        <OtpInput
                            value={otpValue}
                            onChange={setOtpValue}
                            length={6}
                        />
                    </div>

                    <Space>
                        <Button onClick={handleOtpModalCancel}>
                            Отмена
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleOtpVerify}
                            loading={loading}
                            disabled={otpValue.length !== 6 || timeLeft === 'Истекло'}
                        >
                            Подтвердить
                        </Button>
                    </Space>
                </div>
            </Modal>

            {/* Нативный модал для выхода из аккаунта */}
            <Modal
                title="Выход из аккаунта"
                open={logoutModalVisible}
                onCancel={() => setLogoutModalVisible(false)}
                okText="Да, выйти"
                cancelText="Отмена"
                onOk={confirmLogout}
            >
                <p>Вы уверены, что хотите выйти?</p>
            </Modal>
            {/* Модальное окно настройки 2FA */}
            <Modal
                title="Настройка двухфакторной аутентификации"
                open={twoFAModalVisible}
                onCancel={handleTwoFAModalCancel}
                footer={null}
                maskClosable={false}
                width={480}
                centered
            >
                <Alert
                    message="Привязка приложения"
                    description="Для использования вам потребуется скачать приложение-аутентификатор, например, Google Authenticator, 1Password, Authy или другое."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Typography.Text type="secondary">
                    Отсканируйте QR-код из приложения-аутентификатора
                </Typography.Text>

                <Row justify="center" style={{ margin: '24px 0' }}>
                    {qrCodeLoading ? (
                        <Col>
                            <Row justify="center">
                                <Spin size="large"/>
                            </Row>
                            <Row justify="center">
                                <Typography.Text type="secondary" style={{ marginTop: 16 }}>
                                    Загрузка QR-кода...
                                </Typography.Text>
                            </Row>
                        </Col>
                    ) : twoFASetupData?.totp_url ? (
                        <QRCode
                            value={twoFASetupData.totp_url}
                            size={200}
                            errorLevel="M"
                            status={twoFASetupData.totp_url ? "active" : "expired"}
                        />
                    ) : (
                        <Col>
                            <Row justify="center">
                                <QRCode
                                    value="error"
                                    size={200}
                                    status="expired"
                                />
                            </Row>
                            <Row justify="center">
                                <Typography.Text type="danger" style={{ marginTop: 16 }}>
                                    QR-код не удалось загрузить
                                </Typography.Text>
                            </Row>
                        </Col>
                    )}
                </Row>

                <Typography.Text type="secondary">
                    Или добавьте ключ вручную
                </Typography.Text>

                <Row style={{ marginBottom: 16, marginTop: 8 }}>
                    <Col span={24}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Input
                                readOnly
                                value={twoFASecret}
                                style={{ textAlign: 'center', fontFamily: 'monospace' }}
                            />
                            <Button
                                type="primary"
                                icon={<CopyOutlined />}
                                onClick={copySecretToClipboard}
                            />
                        </Space.Compact>
                    </Col>
                </Row>

                <Typography.Text type="secondary">
                    Код из приложения
                </Typography.Text>

                <Row style={{ marginTop: 8, marginBottom: 16 }}>
                    <Col span={24}>
                        <Input.OTP
                            value={twoFACode}
                            onChange={setTwoFACode}
                            length={6}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col span={24}>
                        <Button
                            type="primary"
                            onClick={verifyAndActivateTwoFA}
                            loading={loading}
                            disabled={twoFACode.length !== 6}
                            block
                        >
                            Привязать
                        </Button>
                    </Col>
                </Row>
            </Modal>

            {/* Модальное окно отключения 2FA */}
            <Modal
                title="Отключение двухфакторной аутентификации"
                open={twoFADisableModalVisible}
                onCancel={handleTwoFADisableModalCancel}
                footer={null}
                maskClosable={false}
                width={400}
                centered
            >
                <Alert
                    message="Внимание"
                    description="Отключение двухфакторной аутентификации снизит уровень безопасности вашего аккаунта."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Typography.Text strong>
                    Для подтверждения введите код из приложения-аутентификатора
                </Typography.Text>

                <Row justify="center" style={{ marginTop: 16, marginBottom: 24 }}>
                    <Col>
                        <Input.OTP
                            value={twoFACode}
                            onChange={setTwoFACode}
                            length={6}
                        />
                    </Col>
                </Row>

                <Row justify="end">
                    <Space>
                        <Button onClick={handleTwoFADisableModalCancel}>
                            Отмена
                        </Button>
                        <Button
                            type="primary"
                            danger
                            onClick={disableTwoFA}
                            loading={loading}
                            disabled={twoFACode.length !== 6}
                        >
                            Отключить
                        </Button>
                    </Space>
                </Row>
            </Modal>
        </>
    );
};

export default SecurityPage;